// Repetition notation, on the client.
//
// The server owns the rule; this exists so a coach gets inline feedback instead of a
// round trip, and so the training grid can label each set row with its own target.
// It mirrors models/repscheme.go — same five families, same hyphen rule — and the
// two must not drift.
//
// # THE FIVE NOTATIONS
//
//   fixed      "12"                    every set the same
//   range      "8-10"                  any count inside the band
//   wave       "11,9,7,5"  "10/8/6"    one value per set
//   duration   "30 sec"                time under load
//   distance   "40 m"  "100 ft"        ground covered
//
// # THE HYPHEN IS AMBIGUOUS
//
// "8-10" is a range; "3-1-1-1" is four sets of 3, 1, 1, 1. The rule is arity —
// exactly two parts is a range, three or more is a wave — and it comes from the
// pipeline's own table, where every two-part value ascends. A descending pair like
// "10-8" is therefore a typo, not a third meaning, and is rejected.

export const REP_FAMILY = {
  FIXED: 'fixed',
  RANGE: 'range',
  WAVE: 'wave',
  DURATION: 'duration',
  DISTANCE: 'distance',
};

const DURATION_PATTERN = /^(\d+(?:\.\d+)?)\s*(s|sec|secs|second|seconds)$/i;
const DISTANCE_PATTERN = /^(\d+(?:\.\d+)?)\s*(m|metre|metres|meter|meters|ft|foot|feet)$/i;
const FIXED_PATTERN = /^\d+$/;

/**
 * Parse a rep scheme.
 *
 * @param {string} raw
 * @returns {{family?: string, values?: number[], amount?: number, unit?: string,
 *            raw?: string, error?: string}}
 *   Either a parsed scheme or `{error}` — never a throw, because this runs on every
 *   keystroke in a controlled input.
 */
export function parseRepScheme(raw) {
  const text = String(raw ?? '');
  const collapsed = text.trim().replace(/\s+/g, ' ');

  if (collapsed === '') {
    return { error: 'Reps are required.' };
  }

  // Raw keeps the coach's own spacing; only the ends are trimmed.
  const preserved = text.trim();

  const duration = DURATION_PATTERN.exec(collapsed);
  if (duration) {
    return {
      family: REP_FAMILY.DURATION,
      amount: Number(duration[1]),
      unit: 'sec',
      raw: preserved,
    };
  }

  const distance = DISTANCE_PATTERN.exec(collapsed);
  if (distance) {
    const feet = distance[2].toLowerCase().startsWith('f');
    return {
      family: REP_FAMILY.DISTANCE,
      // Canonical metres, like the server; the unit records what was written.
      amount: feet ? Number(distance[1]) * 0.3048 : Number(distance[1]),
      unit: feet ? 'ft' : 'm',
      raw: preserved,
    };
  }

  if (FIXED_PATTERN.test(collapsed)) {
    return { family: REP_FAMILY.FIXED, values: [Number(collapsed)], raw: preserved };
  }

  // Comma and slash are unambiguous and always mean per-set.
  for (const separator of [',', '/']) {
    if (collapsed.includes(separator)) {
      const values = parseIntList(collapsed, separator);
      if (values.error) return values;
      return { family: REP_FAMILY.WAVE, values: values.values, raw: preserved };
    }
  }

  if (collapsed.includes('-')) {
    const parsed = parseIntList(collapsed, '-');
    if (parsed.error) return parsed;

    if (parsed.values.length === 2) {
      const [low, high] = parsed.values;
      if (low >= high) {
        return {
          error: `"${preserved}" reads as a range but does not ascend. `
            + `Write ${low}/${high} if you meant one value per set.`,
        };
      }
      return { family: REP_FAMILY.RANGE, values: parsed.values, raw: preserved };
    }
    return { family: REP_FAMILY.WAVE, values: parsed.values, raw: preserved };
  }

  return {
    error: `"${preserved}" is not a rep scheme. Use a count (12), a range (8-10), `
      + 'a wave (11,9,7,5), or a duration or distance (30 sec, 40 m).',
  };
}

function parseIntList(value, separator) {
  const parts = value.split(separator);
  const values = [];

  for (const part of parts) {
    const trimmed = part.trim();
    if (trimmed === '') return { error: `"${value}" has an empty value.` };
    if (!FIXED_PATTERN.test(trimmed)) {
      return { error: `"${value}" contains "${trimmed}", which is not a whole number.` };
    }
    values.push(Number(trimmed));
  }

  if (values.length < 2) return { error: `"${value}" needs at least two values.` };
  return { values };
}

/**
 * How many sets a wave prescribes, or 0 when the scheme does not say.
 *
 * A wave names each set, so it constrains the set count; a range or a fixed count
 * says nothing about it.
 */
export function schemeSetCount(scheme) {
  return scheme?.family === REP_FAMILY.WAVE ? scheme.values.length : 0;
}

/**
 * The target for one set, 1-indexed — what the training grid labels each row with.
 *
 * A wave gives each set its own number, which is the whole point of the notation and
 * was invisible while reps was a single integer. Everything else repeats.
 *
 * @returns {string} e.g. "11" for set 1 of "11,9,7,5", "8-10" for a range
 */
export function targetForSet(scheme, setNumber) {
  if (!scheme || scheme.error) return '';

  switch (scheme.family) {
    case REP_FAMILY.WAVE: {
      const value = scheme.values[setNumber - 1];
      // A set beyond what the wave names — the grid renders a spare row — has no
      // prescribed target rather than a wrong one.
      return value === undefined ? '' : String(value);
    }
    case REP_FAMILY.FIXED:
      return String(scheme.values[0]);
    case REP_FAMILY.RANGE:
      return `${scheme.values[0]}-${scheme.values[1]}`;
    case REP_FAMILY.DURATION:
    case REP_FAMILY.DISTANCE:
      return scheme.raw;
    default:
      return '';
  }
}

/**
 * Validate a scheme against a prescribed set count.
 *
 * Mirrors Prescription.Validate: a wave must carry exactly one value per set.
 * Returns an error string, or null.
 */
export function validateRepScheme(raw, sets, limits) {
  const scheme = parseRepScheme(raw);
  if (scheme.error) return scheme.error;

  if (scheme.family === REP_FAMILY.WAVE || scheme.family === REP_FAMILY.RANGE
      || scheme.family === REP_FAMILY.FIXED) {
    const { min, max } = limits ?? { min: 1, max: 100 };
    for (const value of scheme.values) {
      if (value < min || value > max) {
        return `Every rep value must be between ${min} and ${max}.`;
      }
    }
  }

  const prescribed = Number(sets);
  if (scheme.family === REP_FAMILY.WAVE && Number.isFinite(prescribed) && prescribed > 0
      && scheme.values.length !== prescribed) {
    return `"${scheme.raw}" prescribes ${scheme.values.length} sets, but sets is ${prescribed}.`;
  }

  return null;
}

/** Render a scheme for display, falling back to the raw text. */
export function formatRepScheme(raw) {
  const scheme = parseRepScheme(raw);
  return scheme.error ? String(raw ?? '') : scheme.raw;
}

/**
 * What an entry prescribes, for display — the scheme when it has one, and the
 * legacy integer when it does not.
 *
 * The client mirror of ExerciseEntry.RepsPrescribed. Any screen that renders
 * `entry.reps` directly is showing the projection rather than the prescription:
 * "4×8" for a prescribed "8-10", "3×11" for a wave of "11,9,7".
 *
 * @param {{repScheme?: string, reps?: number}} entry
 */
export function repsPrescribed(entry) {
  const scheme = entry?.repScheme;
  if (scheme) return formatRepScheme(scheme);
  return entry?.reps ? String(entry.reps) : '';
}

/**
 * How many reps a single prescription line commits to, as a range.
 *
 * # WHY A RANGE AND NOT A NUMBER
 *
 * A rep scheme is "6-8" or "11,9,7,5" or "45 sec". The density footer used to
 * refuse a rep total outright, because collapsing a range to one figure invents
 * precision — a summed midpoint reads exactly as exact as a number someone
 * counted. A range does not pick a value, so it carries the prescription's own
 * uncertainty instead of hiding it: "6-8" over four sets is 24–32, and that is
 * the honest thing to say.
 *
 * Families divide cleanly:
 *   fixed  "8"          low = high = sets × 8         one value, every set
 *   range  "6-8"        low = sets × 6, high = sets × 8
 *   wave   "11,9,7,5"   low = high = 11+9+7+5         the list IS the sets, so
 *                                                     it is summed, not multiplied
 *   duration / distance                              not reps — returns null
 *
 * @param {{sets?: number, repScheme?: string, reps?: number}} line
 * @returns {{low: number, high: number} | null} null when the line prescribes no
 *   reps at all (timed, measured, or unparseable) — the caller leaves it out of
 *   the rep figure rather than counting it as zero.
 */
export function repsForLine(line) {
  const sets = Number(line?.sets);
  const raw = line?.repScheme;

  // A line with no scheme but a legacy integer count is a fixed prescription.
  if (!raw) {
    const reps = Number(line?.reps);
    if (!Number.isFinite(reps) || reps <= 0 || !Number.isFinite(sets) || sets <= 0) {
      return null;
    }
    const total = sets * reps;
    return { low: total, high: total };
  }

  const scheme = parseRepScheme(raw);
  if (scheme.error) return null;

  switch (scheme.family) {
    case REP_FAMILY.FIXED:
    case REP_FAMILY.RANGE: {
      // Per-set values: multiplied by the set count. A range's two values are
      // [low, high]; a fixed's single value is both.
      if (!Number.isFinite(sets) || sets <= 0) return null;
      const values = scheme.values;
      const low = values[0];
      const high = values[values.length - 1];
      return { low: sets * low, high: sets * high };
    }
    case REP_FAMILY.WAVE: {
      // One value per set, already the whole line's work. Summed, never scaled —
      // the list length is the set count.
      const total = scheme.values.reduce((sum, value) => sum + value, 0);
      return { low: total, high: total };
    }
    default:
      // Duration and distance are load, not reps.
      return null;
  }
}
